import { useId, type ReactNode, type SelectHTMLAttributes } from 'react'
import { cn } from '../../utils/cn'
import { FormField } from './FormField'
import { CONTROL_BASE, controlTone, describedBy } from './fieldStyles'

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: ReactNode
  id?: string
  hint?: ReactNode
  error?: ReactNode
  /** Shown as a disabled first option when the value is empty. */
  placeholder?: string
  containerClassName?: string
  children: ReactNode
}

export function Select({
  label,
  id,
  hint,
  error,
  placeholder,
  required,
  className,
  containerClassName,
  children,
  ...rest
}: SelectProps) {
  const autoId = useId()
  const fieldId = id ?? autoId

  return (
    <FormField
      id={fieldId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      <div className="relative">
        <select
          {...rest}
          id={fieldId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(fieldId, hint, error)}
          className={cn(CONTROL_BASE, controlTone(Boolean(error)), 'appearance-none pr-12', className)}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {children}
        </select>
        <span className="material-symbols-outlined pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[20px] text-outline">
          expand_more
        </span>
      </div>
    </FormField>
  )
}
